'use client';

import { Dialog, DialogActions, DialogContent, DialogTitle } from '@andritzot/metris-web-ui/feedback/dialog';
import { DataGrid, type GridColDef, type GridRowSelectionModel } from '@andritzot/metris-web-ui/mui-x/data-grid';
import { type Dispatch, type SetStateAction, useState } from 'react';

type AddItemDialogProps<T extends object> = {
  visible: boolean;
  setVisible: Dispatch<SetStateAction<boolean>>;
  values: T[];
  isLoading: boolean;
  getRowId: (row: T) => string | number;
  columns: GridColDef<T>[];
  onAddAsync: (selected: T[]) => Promise<void> | void;
};

const AddItemDialog = <T extends object>({
  visible,
  setVisible,
  values,
  isLoading,
  getRowId,
  columns,
  onAddAsync,
}: AddItemDialogProps<T>) => {
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });

  const handleClose = () => {
    setVisible(false);
    setSelectionModel({ type: 'include', ids: new Set() });
  };

  const handleAdd = async () => {
    const { type, ids } = selectionModel;
    const selected = values.filter((row) => (type === 'exclude' ? !ids.has(getRowId(row)) : ids.has(getRowId(row))));
    await onAddAsync(selected);
    handleClose();
  };

  return (
    <Dialog open={visible} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle title="Add items" closable onClose={handleClose} />
      <DialogContent>
        <DataGrid
          rows={values}
          columns={columns}
          getRowId={getRowId}
          loading={isLoading}
          checkboxSelection
          disableRowSelectionOnClick
          pageSizeOptions={[10]}
          pagination
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          disableColumnMenu
        />
      </DialogContent>
      <DialogActions
        primaryAction={{
          label: 'Add',
          onClick: handleAdd,
          disabled:
            (selectionModel.type === 'exclude' ? values.length - selectionModel.ids.size : selectionModel.ids.size) ===
            0,
        }}
        secondaryAction={{ label: 'Close', onClick: handleClose }}
      />
    </Dialog>
  );
};

export default AddItemDialog;
